import { useRef, useState, type ChangeEvent } from "react";

type VideoStageProps = {
  src: string;
  poster: string;
  title: string;
  episode: number;
  total: number;
};

function formatTime(value: number): string {
  if (!Number.isFinite(value) || value < 0) return "00:00";
  const minutes = Math.floor(value / 60);
  const seconds = Math.floor(value % 60);
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

export default function VideoStage({ src, poster, title, episode, total }: VideoStageProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(80);
  const [muted, setMuted] = useState(false);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      void video.play().catch(() => setPlaying(false));
    } else {
      video.pause();
    }
  };

  const handleSeek = (event: ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    const next = Number(event.target.value);
    setCurrent(next);
    if (video) video.currentTime = next;
  };

  const handleVolume = (event: ChangeEvent<HTMLInputElement>) => {
    const next = Number(event.target.value);
    setVolume(next);
    if (videoRef.current) {
      videoRef.current.volume = next / 100;
      videoRef.current.muted = next === 0;
    }
    setMuted(next === 0);
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    const next = !video.muted;
    video.muted = next;
    setMuted(next);
  };

  const toggleFullscreen = () => {
    const shell = shellRef.current;
    if (!shell) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void shell.requestFullscreen().catch(() => undefined);
    }
  };

  return (
    <div
      ref={shellRef}
      className="group relative w-full overflow-hidden rounded-2xl border border-foreground-50/10 bg-black shadow-2xl shadow-background-950/80"
    >
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        preload="metadata"
        playsInline
        className="aspect-video w-full cursor-pointer bg-black object-cover"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onTimeUpdate={(event) => setCurrent(event.currentTarget.currentTime)}
        onLoadedMetadata={(event) => setDuration(event.currentTarget.duration)}
        onEnded={() => setPlaying(false)}
        onClick={togglePlay}
      />

      {/* 顶部信息蒙层 */}
      <div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between gap-3 bg-gradient-to-b from-background-950/85 to-transparent px-3 py-2.5 md:px-4 md:py-3">
        <p className="min-w-0 truncate text-xs font-medium text-foreground-100 md:text-sm">
          《{title}》第 {episode} 集
        </p>
        <div className="flex shrink-0 items-center gap-1.5">
          <span className="rounded border border-primary-400/50 px-1.5 py-0.5 text-[10px] font-semibold text-primary-300">
            1080P
          </span>
          <span className="rounded bg-foreground-50/15 px-1.5 py-0.5 text-[10px] text-foreground-100 backdrop-blur-sm">
            共 {total} 集
          </span>
        </div>
      </div>

      {/* 中央播放按钮 */}
      {!playing && (
        <button
          type="button"
          onClick={togglePlay}
          aria-label={`播放《${title}》第 ${episode} 集`}
          className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-primary-500/95 text-background-950 shadow-2xl shadow-background-950/70 transition-transform hover:scale-105 md:h-20 md:w-20"
        >
          <i className="ri-play-fill text-3xl md:text-4xl" aria-hidden="true" />
        </button>
      )}

      {/* 底部控制条 */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-background-950/95 via-background-950/70 to-transparent px-3 pb-2.5 pt-8 md:px-4 md:pb-3">
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.1}
          value={current}
          onChange={handleSeek}
          aria-label="播放进度"
          className="h-1 w-full cursor-pointer appearance-none rounded-full bg-foreground-50/25 accent-primary-500"
        />
        <div className="mt-2 flex items-center gap-2 md:gap-3">
          <button
            type="button"
            onClick={togglePlay}
            aria-label={playing ? "暂停" : "播放"}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-foreground-50 transition-colors hover:bg-foreground-50/10 hover:text-primary-300"
          >
            <i className={playing ? "ri-pause-fill text-lg" : "ri-play-fill text-lg"} aria-hidden="true" />
          </button>

          <span className="shrink-0 font-mono text-[11px] text-foreground-200 md:text-xs">
            {formatTime(current)} / {formatTime(duration)}
          </span>

          <div className="ml-auto flex items-center gap-1 md:gap-2">
            <div className="hidden items-center gap-1.5 sm:flex">
              <button
                type="button"
                onClick={toggleMute}
                aria-label={muted ? "取消静音" : "静音"}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-foreground-200 transition-colors hover:bg-foreground-50/10 hover:text-primary-300"
              >
                <i
                  className={muted ? "ri-volume-mute-line text-base" : "ri-volume-up-line text-base"}
                  aria-hidden="true"
                />
              </button>
              <input
                type="range"
                min={0}
                max={100}
                value={muted ? 0 : volume}
                onChange={handleVolume}
                aria-label="音量"
                className="h-1 w-16 cursor-pointer appearance-none rounded-full bg-foreground-50/25 accent-primary-500"
              />
            </div>

            <button
              type="button"
              aria-label="分享本集"
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-foreground-200 transition-colors hover:bg-foreground-50/10 hover:text-primary-300"
            >
              <i className="ri-share-forward-line text-base" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={toggleFullscreen}
              aria-label="全屏播放"
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-foreground-200 transition-colors hover:bg-foreground-50/10 hover:text-primary-300"
            >
              <i className="ri-fullscreen-line text-base" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}